import { isValidEmail } from 'common-utils';
import { Nullable } from 'ox-common-types';
import React from 'react';
import { snapshot } from 'valtio';
import settingsService from '../services';
import {
  closeInviteUserModal,
  setEmails,
} from '../store-actions/invite-user-store-actions';
import InviteMemberStore from '../stores/invite-user-store';
import { isAlreadyAMember, isAlreadyInvited } from '../utils/members-utils';
import { setPendingInvitationMembersInStore } from '../store-actions/invite-user-store-actions';
import {
  SCOPE_SELECTION_OPTIONS,
  ScopeOptions,
} from '../types/user-scope-types';
import {
  InvitationInput,
  PendingInvitationMember,
  Role,
} from '../types/members-types';
import {
  ScopeConfig,
  ScopeNames,
  ScopeTypes,
  buildScopesString,
} from '@oxappsec/ox-unified-permissions/src/scopes';

export const createInvitation = async (
  invitationInput: InvitationInput,
): Promise<void> => {
  InviteMemberStore.isSubmitButtonLoading = true;

  const createInvitationResponse: Nullable<PendingInvitationMember> =
    await settingsService.createInvitation.execute(invitationInput);

  if (!createInvitationResponse) {
    InviteMemberStore.isSubmitButtonLoading = false;
    return;
  }

  const { pendingInvitationMembers: pendingInvitations } =
    snapshot(InviteMemberStore);

  InviteMemberStore.pendingInvitationMembers = [
    ...pendingInvitations,
    createInvitationResponse,
  ];
  InviteMemberStore.isSubmitButtonLoading = false;
  closeInviteUserModal();
};

export const createMultipleInvitations = async (
  invitationsInput: InvitationInput[],
): Promise<void> => {
  InviteMemberStore.isSubmitButtonLoading = true;

  const createMultipleInvitationsResponse: Nullable<PendingInvitationMember[]> =
    await settingsService.createMultipleInvitations.execute(invitationsInput);

  if (!createMultipleInvitationsResponse) {
    InviteMemberStore.isSubmitButtonLoading = false;
    return;
  }

  const { pendingInvitationMembers: pendingInvitations } =
    snapshot(InviteMemberStore);

  InviteMemberStore.pendingInvitationMembers = [
    ...pendingInvitations,
    ...createMultipleInvitationsResponse,
  ];
  InviteMemberStore.isSubmitButtonLoading = false;
  closeInviteUserModal();
};

export const createInvitations = async () => {
  const { newMembersData, currentScopeOptionSelected } =
    snapshot(InviteMemberStore);
  const userOnlyOptionSelected =
    currentScopeOptionSelected ===
    SCOPE_SELECTION_OPTIONS[ScopeOptions.UserOnly].value;
  const customScopeOptionSelected =
    currentScopeOptionSelected ===
    SCOPE_SELECTION_OPTIONS[ScopeOptions.CustomScope].value;

  const invitationsList: InvitationInput[] = newMembersData.emails.map(
    inviteeEmail => {
      let scopes: string | null = null;

      if (userOnlyOptionSelected || customScopeOptionSelected) {
        const appOwnersScopeItems = userOnlyOptionSelected
          ? [inviteeEmail]
          : newMembersData.selectedAppOwnersScopeItems;
        const scopesConfig = generateScopeConfig([
          { scopeName: ScopeNames.AppOwner, values: appOwnersScopeItems },
          {
            scopeName: ScopeNames.Tags,
            values: newMembersData.selectedTagsScopeItems,
          },
        ]);

        scopes = scopesConfig ? buildScopesString(scopesConfig) : null;
      }

      return {
        inviteeEmail,
        inviteeRoles: newMembersData.roles.map(role => role.id),
        scopes: scopes,
      };
    },
  );

  const createMultipleInvitationsResponse =
    await settingsService.createMultipleInvitations.execute(invitationsList);

  if (!createMultipleInvitationsResponse) {
    return;
  }

  const { pendingInvitationMembers: pendingInvitations } =
    snapshot(InviteMemberStore);

  const pendingInvitationMembers = [
    ...pendingInvitations,
    ...createMultipleInvitationsResponse,
  ];

  setPendingInvitationMembersInStore(pendingInvitationMembers);

  return createMultipleInvitationsResponse;
};

export const onEmailInputChange = (
  event: React.SyntheticEvent<Element, Event>,
  newInputValue: string,
) => {
  const { newMembersData, hasErrorMessage, errorMessage } =
    snapshot(InviteMemberStore);
  const { emails } = newMembersData;

  let canAddEmail = false;
  const isInputWithDelimiter =
    newInputValue.includes(',') || newInputValue.includes(';');
  if (newInputValue.length < 6) {
    InviteMemberStore.hasErrorMessage = false;
    InviteMemberStore.errorMessage = '';
  } else if (!isInputWithDelimiter && !isValidEmail(newInputValue.trim())) {
    InviteMemberStore.hasErrorMessage = true;
    InviteMemberStore.errorMessage = 'Please enter a valid email address';
  } else if (isAlreadyInvited(newInputValue.trim())) {
    InviteMemberStore.hasErrorMessage = true;
    InviteMemberStore.errorMessage = 'User already has a pending invitation';
  } else if (isAlreadyAMember(newInputValue.trim())) {
    InviteMemberStore.hasErrorMessage = true;
    InviteMemberStore.errorMessage = 'User is already a member';
  } else {
    InviteMemberStore.hasErrorMessage = false;
    InviteMemberStore.errorMessage = '';
    canAddEmail = true;
  }

  // this is to prevent input being erased when pressing enter on an invalid input
  if (event.type === 'keydown' && !canAddEmail) {
    InviteMemberStore.hasErrorMessage = hasErrorMessage;
    InviteMemberStore.errorMessage = errorMessage;
    return null;
  }
  if (!newInputValue && canAddEmail) {
    return newInputValue;
  }

  if (newInputValue.includes(';')) {
    setEmailsByDelimiter(';', newInputValue, emails);
    return '';
  }
  if (newInputValue.includes(',')) {
    if (newInputValue.includes(';')) {
      setEmailsByDelimiter(';', newInputValue, emails);
      return '';
    }
    setEmailsByDelimiter(',', newInputValue, emails);
    return '';
  }
  if (!!newInputValue.trim() && newInputValue.endsWith(' ') && canAddEmail) {
    setEmails([...emails, newInputValue.trim()]);
    return '';
  }

  return newInputValue;
};

const setEmailsByDelimiter = (
  delimiter: string,
  emailsString: string,
  emails: string[],
) => {
  const newEmails = emailsString.split(delimiter).filter(value => !!value);
  const trimmedEmails = newEmails.map(email => email.trim());
  setEmails([...emails, ...trimmedEmails]);
};

export const addEmailToEmailsList = (
  event: React.SyntheticEvent<Element, Event>,
  newValue: string[],
  email: string,
  selectedRoles: Role[],
) => {
  const { hasErrorMessage } = snapshot(InviteMemberStore);
  const canAddEmail = !hasErrorMessage && isValidEmail(email);
  // @ts-ignore
  if (event.type === 'click' || event?.key === 'Backspace') {
    setEmails(newValue);
    return false;
  }
  // This is to prevent from adding invalid emails to the list when pressing enter
  if (!canAddEmail && event.type !== 'click') {
    return false;
  }
  setEmails(newValue);
  return true;
};

export const onEmailInputBlur = (
  event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  email: string,
) => {
  const { hasErrorMessage, newMembersData } = snapshot(InviteMemberStore);
  const { emails } = newMembersData;
  const canAddEmail = !hasErrorMessage && isValidEmail(email);
  if (canAddEmail) {
    setEmails([...emails, email]);
  } else if (!!email && !isValidEmail(email)) {
    InviteMemberStore.hasErrorMessage = true;
    InviteMemberStore.errorMessage = 'Please enter a valid email address';
  }
  return canAddEmail;
};

export const generateScopeConfig = (
  scopeItems: {
    scopeName: ScopeNames;
    values?: Nullable<string[]>;
  }[],
) => {
  return scopeItems.reduce((acc: ScopeConfig[], scopeItem) => {
    if (!scopeItem.values || scopeItem.values.length === 0) {
      return acc;
    }
    acc.push({
      scopeName: scopeItem.scopeName,
      scopeType: ScopeTypes.MultiValue,
      values: scopeItem.values,
    });
    return acc;
  }, []);
};
